/* eslint-disable jira/i18n/no-export-define-message */
import React, { useEffect, useMemo } from 'react';
import type { RovoConfigProviderEntryProps } from '@jira/ai-rovo_actions-shared-types/src/RovoConfigProviderEntry.types.tsx';
import type { RovoConfiguration } from '@jira/ai-rovo_actions-shared-types/src/RovoConfiguration.types.tsx';
import type { RovoAction } from '@jira/ai-rovo_actions-shared-types/src/RovoAction.types.tsx';
import AiChatIcon from '@atlaskit/icon/core/ai-chat';
import { token } from '@atlaskit/tokens';
import { defineMessage, type MessageDescriptor, useIntl } from '@atlassian/jira-intl';
import { HandleActionType } from '@atlassian/rovo-conversation-actions/types';
import type { PermissionsConfigProviderEntryEntrypointProps } from './PermissionsConfigProviderEntry.entrypoint.types.tsx';
import { useExplicitPermissionsSkillPrompt } from './useExplicitPermissionsSkillPrompt.tsx';

export const whoCanAccessSpaceWhatRolesPrompt: MessageDescriptor = defineMessage({
	defaultMessage: 'Who can access this space and what roles do they have?',
	description: 'Permissions AI prompt Who can access this space and what roles do they have?',
	id: 'ai-rovo.permissions.who-can-access-space-what-roles',
});

export const whyUserCannotViewEditWorkItemsPrompt: MessageDescriptor = defineMessage({
	defaultMessage: "Why can't a user view or edit work items in this space?",
	description: "Permissions AI prompt Why can't a user view or edit work items in this space?",
	id: 'ai-rovo.permissions.why-user-cannot-view-edit-work-items',
});

export const whoHasSpaceAdminOrManageSprintsPermissionsPrompt: MessageDescriptor = defineMessage({
	defaultMessage: 'Who has space admin or sprint management permissions?',
	description: 'Permissions AI prompt Who has space admin or sprint management permissions?',
	id: 'ai-rovo.permissions.who-has-space-admin-sprint-management-permissions',
});

export const PermissionsConfigProvider = ({
	setConfiguration,
}: PermissionsConfigProviderEntryEntrypointProps): null => {
	const { formatMessage } = useIntl();
	const { formatExplicitPermissionsSkillPrompt } = useExplicitPermissionsSkillPrompt();

	const config = useMemo<RovoConfiguration>(() => {
		return {
			getActions: async () => {
				const actions: RovoAction[] = [
					{
						action: {
							id: 'who-can-access-space-what-roles',
							title: formatMessage(whoCanAccessSpaceWhatRolesPrompt),
							staticPrompt: formatExplicitPermissionsSkillPrompt(whoCanAccessSpaceWhatRolesPrompt),
							icon: () => <AiChatIcon label="" color={token('color.icon')} />,
							handleType: HandleActionType.Skill,
						},
					},
					{
						action: {
							id: 'why-user-cannot-view-edit-work-items',
							title: formatMessage(whyUserCannotViewEditWorkItemsPrompt),
							staticPrompt: formatExplicitPermissionsSkillPrompt(
								whyUserCannotViewEditWorkItemsPrompt,
							),
							icon: () => <AiChatIcon label="" color={token('color.icon')} />,
							handleType: HandleActionType.Skill,
						},
					},
					{
						action: {
							id: 'who-has-space-admin-sprint-management-permissions',
							title: formatMessage(whoHasSpaceAdminOrManageSprintsPermissionsPrompt),
							staticPrompt: formatExplicitPermissionsSkillPrompt(
								whoHasSpaceAdminOrManageSprintsPermissionsPrompt,
							),
							icon: () => <AiChatIcon label="" color={token('color.icon')} />,
							handleType: HandleActionType.Skill,
						},
					},
				];

				return Promise.all(
					actions.map(async ({ action, entryPointActions }) => {
						await entryPointActions?.load();
						return action;
					}),
				);
			},
			tags: {
				skillRegistryTags: ['jira-permissions'],
			},
		};
	}, [formatMessage, formatExplicitPermissionsSkillPrompt]);

	useEffect(() => {
		// eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-data-to-parent
		setConfiguration(config);
	}, [config, setConfiguration]);

	return null;
};

export const PermissionsConfigProviderEntry = ({
	props: { setConfiguration },
}: RovoConfigProviderEntryProps): React.JSX.Element => (
	<PermissionsConfigProvider setConfiguration={setConfiguration} />
);

export default PermissionsConfigProviderEntry;
